/**
 * FriendlyEventError — turns raw e-invoicing event payloads into business-readable UI.
 *
 * This file is the React / Fluent UI rendering layer only. All the deterministic
 * parsing, translation catalogs and grouping logic live in `friendlyErrorParsing.ts`
 * (which is fluentui-free and unit-tested). To onboard a new country/provider, edit the
 * catalogs there — see the PLAYBOOK at the top of that file.
 */
import React from 'react';
import { makeStyles, shorthands, tokens, Badge, Tooltip } from '@fluentui/react-components';
import type { FluentIcon } from '@fluentui/react-icons';
import {
    ChevronDownRegular,
    ChevronRightRegular,
    ErrorCircleFilled,
    WarningFilled,
    CopyRegular,
    SendFilled,
    CheckmarkCircleFilled,
    ClockFilled,
    ArrowSyncFilled,
    ArrowDownloadFilled,
    SubtractCircleFilled,
    InfoFilled,
} from '@fluentui/react-icons';
import {
    parseEventErrors,
    groupErrors,
    parseEventStatus,
    groupKey,
    humanizeLabel,
    BadgeColor,
    EventStatus,
    GroupedError,
} from './friendlyErrorParsing';

// Icon per known status keyword (keys mirror StatusMeta in friendlyErrorParsing.ts).
// Kept here so the parsing module stays free of Fluent UI imports.
const StatusIcons: { [key: string]: FluentIcon } = {
    sent: SendFilled,
    delivered: CheckmarkCircleFilled,
    succeeded: CheckmarkCircleFilled,
    success: CheckmarkCircleFilled,
    completed: CheckmarkCircleFilled,
    published: CheckmarkCircleFilled,
    accepted: CheckmarkCircleFilled,
    received: ArrowDownloadFilled,
    synchronousresponse: ArrowDownloadFilled,
    inprogress: ClockFilled,
    processing: ArrowSyncFilled,
    started: ArrowSyncFilled,
    pending: ClockFilled,
    queued: ClockFilled,
    batched: ClockFilled,
    retrying: ArrowSyncFilled,
    warning: WarningFilled,
    skipped: SubtractCircleFilled,
    failed: ErrorCircleFilled,
    error: ErrorCircleFilled,
    rejected: ErrorCircleFilled,
};

const useStyles = makeStyles({
    list: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('8px'),
    },
    statusLine: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        ...shorthands.gap('8px'),
        ...shorthands.padding('6px', '10px'),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.borderLeft('4px', 'solid', tokens.colorNeutralStroke1),
    },
    statusPill: {
        display: 'inline-flex',
        alignItems: 'center',
        ...shorthands.gap('6px'),
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
    },
    kvChips: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        ...shorthands.gap('6px'),
    },
    kvChip: {
        display: 'inline-flex',
        alignItems: 'baseline',
        ...shorthands.gap('4px'),
        ...shorthands.padding('2px', '8px'),
        ...shorthands.borderRadius(tokens.borderRadiusCircular),
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        fontSize: tokens.fontSizeBase200,
    },
    kvLabel: {
        color: tokens.colorNeutralForeground3,
    },
    kvValue: {
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightSemibold,
    },
    card: {
        ...shorthands.border('1px', 'solid', tokens.colorPaletteRedBorder1),
        ...shorthands.borderLeft('4px', 'solid', tokens.colorPaletteRedBorderActive),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        backgroundColor: tokens.colorPaletteRedBackground1,
        ...shorthands.padding('12px', '14px'),
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('8px'),
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        ...shorthands.gap('8px'),
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase300,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap('6px'),
        marginTop: '2px',
    },
    ruleBox: {
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius(tokens.borderRadiusSmall),
        ...shorthands.padding('8px', '10px'),
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
    },
    detailsBox: {
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderLeft('3px', 'solid', tokens.colorPaletteRedBorderActive),
        ...shorthands.borderRadius(tokens.borderRadiusSmall),
        ...shorthands.padding('8px', '10px'),
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
    },
    detailsListUl: {
        margin: '4px 0 0 0',
        paddingLeft: '18px',
    },
    source: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    translatedNote: {
        fontSize: tokens.fontSizeBase100,
        fontStyle: 'italic',
        color: tokens.colorNeutralForeground3,
        marginTop: '2px',
    },
    locations: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
    },
    locationList: {
        margin: '4px 0 0 0',
        paddingLeft: '18px',
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
    },
    techToggle: {
        display: 'inline-flex',
        alignItems: 'center',
        ...shorthands.gap('4px'),
        cursor: 'pointer',
        color: tokens.colorBrandForegroundLink,
        fontSize: tokens.fontSizeBase200,
        userSelect: 'none',
        width: 'fit-content',
    },
    techBox: {
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.borderRadius(tokens.borderRadiusSmall),
        ...shorthands.padding('8px', '10px'),
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: tokens.fontSizeBase200,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: tokens.colorNeutralForeground2,
    },
    copyBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        ...shorthands.gap('4px'),
        cursor: 'pointer',
        color: tokens.colorBrandForegroundLink,
        fontSize: tokens.fontSizeBase200,
        background: 'none',
        ...shorthands.border('0'),
        ...shorthands.padding('0'),
        marginTop: '4px',
    },
});

function ErrorCard({ err, severity }: { err: GroupedError; severity: 'error' | 'warning' }) {
    const styles = useStyles();
    const [showTech, setShowTech] = React.useState(false);
    const [copied, setCopied] = React.useState(false);

    const copyTech = () => {
        const codeLine = [
            err.codes.length ? `Code ${err.codes.join(', ')}` : '',
            err.ruleCodes.length ? `Rule ${err.ruleCodes.join(', ')}` : '',
            err.providerCodes.length ? `Provider ${err.providerCodes.join(', ')}` : '',
        ].filter(Boolean).join(' | ');
        const text = `${codeLine}\n${err.rawMessages.join('\n\n')}`;
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            });
        }
    };

    const translatedNote = err.translationSource === 'phrase'
        ? "Translated from the provider's message — original in Technical details"
        : 'Translated from the provider/authority code — original message in Technical details';

    // Derive the whole card theme from severity so a warning is not presented as an error:
    // background, borders, the details accent and the code/count badges all follow suit.
    const theme = severity === 'warning'
        ? {
            icon: <WarningFilled aria-hidden="true" style={{ color: tokens.colorPaletteYellowForeground1, fontSize: 18, flexShrink: 0 }} />,
            border: tokens.colorPaletteYellowBorder1,
            accent: tokens.colorPaletteYellowBorderActive,
            background: tokens.colorPaletteYellowBackground1,
            badge: 'warning' as const,
        }
        : {
            icon: <ErrorCircleFilled aria-hidden="true" style={{ color: tokens.colorPaletteRedForeground1, fontSize: 18, flexShrink: 0 }} />,
            border: tokens.colorPaletteRedBorder1,
            accent: tokens.colorPaletteRedBorderActive,
            background: tokens.colorPaletteRedBackground1,
            badge: 'danger' as const,
        };

    return (
        <div
            className={styles.card}
            style={{ borderColor: theme.border, borderLeftColor: theme.accent, backgroundColor: theme.background }}
            role="group"
            aria-label="Error detail"
        >
            <div className={styles.header}>
                {theme.icon}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className={styles.title}>{err.primaryTitle}</span>
                    <div className={styles.chips}>
                        {err.codes.map((c) => (
                            <Tooltip key={`c-${c}`} content="Internal error code" relationship="label">
                                <Badge appearance="tint" color={theme.badge} size="small">Code {c}</Badge>
                            </Tooltip>
                        ))}
                        {err.ruleCodes.map((r) => (
                            <Tooltip key={`r-${r}`} content="Compliance rule that was violated" relationship="label">
                                <Badge appearance="tint" color="informative" size="small">{r}</Badge>
                            </Tooltip>
                        ))}
                        {err.providerCodes.map((p) => (
                            <Tooltip key={`p-${p}`} content="Tax-authority / provider error code" relationship="label">
                                <Badge appearance="tint" color="warning" size="small">{p}</Badge>
                            </Tooltip>
                        ))}
                        {err.count > 1 && (
                            <Tooltip content={`This error was reported ${err.count} times in this response`} relationship="label">
                                <Badge appearance="filled" color={theme.badge} size="small">&times;{err.count}</Badge>
                            </Tooltip>
                        )}
                    </div>
                    {err.translated && (
                        <span className={styles.translatedNote}>{translatedNote}</span>
                    )}
                </div>
            </div>

            {!err.translated && err.detailsList.length === 1 && (
                <div className={styles.detailsBox} style={{ borderLeftColor: theme.accent }}>
                    <strong>Details:&nbsp;</strong>{err.detailsList[0]}
                </div>
            )}
            {!err.translated && err.detailsList.length > 1 && (
                <div className={styles.detailsBox} style={{ borderLeftColor: theme.accent }}>
                    <strong>Details:</strong>
                    <ul className={styles.detailsListUl}>
                        {err.detailsList.map((d, i) => (<li key={i}>{d}</li>))}
                    </ul>
                </div>
            )}

            {err.businessRules.map((rule, i) => (
                <div key={i} className={styles.ruleBox}>
                    <strong>Rule:&nbsp;</strong>{rule}
                </div>
            ))}

            {err.locations.length > 0 && (
                <div className={styles.locations}>
                    <strong>{err.locations.length > 1 ? `Affected locations (${err.locations.length}):` : 'Location:'}</strong>
                    <ul className={styles.locationList}>
                        {err.locations.map((loc, i) => (
                            <li key={i}>{loc}</li>
                        ))}
                    </ul>
                </div>
            )}

            {err.sources.length > 0 && <div className={styles.source}>Reported by {err.sources.join(', ')}</div>}

            <div
                className={styles.techToggle}
                role="button"
                tabIndex={0}
                aria-expanded={showTech}
                onClick={() => setShowTech(!showTech)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowTech(!showTech); } }}
            >
                {showTech ? <ChevronDownRegular fontSize={14} /> : <ChevronRightRegular fontSize={14} />}
                Technical details
            </div>
            {showTech && (
                <>
                    <div className={styles.techBox}>{err.rawMessages.join('\n\n')}</div>
                    <button type="button" className={styles.copyBtn} onClick={copyTech}>
                        <CopyRegular fontSize={14} />
                        {copied ? 'Copied!' : 'Copy technical details'}
                    </button>
                </>
            )}
        </div>
    );
}

interface FriendlyEventErrorProps {
    content: string;
    severity?: 'error' | 'warning';
}

// Fluent color tokens for each status color (icon foreground + card left accent).
function statusColorTokens(color: BadgeColor): { fg: string; accent: string } {
    switch (color) {
        case 'success': return { fg: tokens.colorPaletteGreenForeground1, accent: tokens.colorPaletteGreenBorderActive };
        case 'danger': return { fg: tokens.colorPaletteRedForeground1, accent: tokens.colorPaletteRedBorderActive };
        case 'warning': return { fg: tokens.colorPaletteYellowForeground1, accent: tokens.colorPaletteYellowBorderActive };
        case 'brand': return { fg: tokens.colorBrandForeground1, accent: tokens.colorBrandStroke1 };
        case 'subtle': return { fg: tokens.colorNeutralForeground3, accent: tokens.colorNeutralStroke1 };
        case 'informative':
        default: return { fg: tokens.colorNeutralForeground2BrandHover, accent: tokens.colorBrandStroke2 };
    }
}

// Renders a colored status pill + key/value chips for a non-error (success/sent/...) event.
function EventStatusLine({ status }: { status: EventStatus }) {
    const styles = useStyles();
    const color: BadgeColor = status.statusColor;
    const accent = statusColorTokens(color).accent;
    const Icon = status.statusKey ? (StatusIcons[status.statusKey] ?? InfoFilled) : InfoFilled;

    // Long alphanumeric values (receipt / reference IDs) read better in monospace.
    const isIdValue = (label: string, value: string) =>
        /id$|reference|receipt|number/i.test(label) || /^[A-Z0-9-]{10,}$/.test(value);

    return (
        <div className={styles.statusLine} style={{ borderLeftColor: accent }} role="group" aria-label="Event status">
            {status.statusLabel && (
                <Badge
                    appearance="filled"
                    color={color}
                    size="large"
                    icon={<Icon aria-hidden="true" />}
                >
                    {status.statusLabel}
                </Badge>
            )}
            {status.pairs.length > 0 && (
                <span className={styles.kvChips}>
                    {status.pairs.map((p, i) => (
                        <span key={i} className={styles.kvChip}>
                            <span className={styles.kvLabel}>{humanizeLabel(p.label)}:</span>
                            <span
                                className={styles.kvValue}
                                style={isIdValue(p.label, p.value) ? { fontFamily: tokens.fontFamilyMonospace } : undefined}
                            >
                                {p.value}
                            </span>
                        </span>
                    ))}
                </span>
            )}
        </div>
    );
}

// Renders a friendly, business-readable card for EventErrors payloads.
// Falls back to a status pill/chips, or the raw (encoding-fixed) text, for other events.
function FriendlyEventError({ content, severity = 'error' }: FriendlyEventErrorProps) {
    const styles = useStyles();
    const parsed = parseEventErrors(content);

    if (!parsed) {
        const status = parseEventStatus(content);
        if (status.hasVisual) {
            return <EventStatusLine status={status} />;
        }
        return <span>{status.text}</span>;
    }

    const grouped = groupErrors(parsed);

    return (
        <div className={styles.list}>
            {grouped.map((err) => (
                <ErrorCard key={groupKey(err)} err={err} severity={severity} />
            ))}
        </div>
    );
}

export default FriendlyEventError;
